import { useNavigate } from "@tanstack/react-router";
import { App, Modal } from "antd5";

import Button from "components/ui/button/Button";
import { appRoutes } from "lib/appRoutes";
import { useDeleteCustomerDocuments } from "lib/hooks/api/customer_documents/useDeleteCustomerDocuments";
import { useDeleteBidReport } from "../../../lib/hooks/api/bid/bid_reports/useDeleteBidReport";
import { EventNames, useTracking } from "../../../lib/tracking";
import { DeleteDocumentType } from "../../../lib/types/graphQLEnums";

import css from "./ReportModals.module.scss";

type Props = {
  bidReportId: string;
  bidId: string | undefined | null;
  isOpen: boolean;
  onClose: () => void;
};
export function DeleteBidReportModal({ bidReportId, isOpen, onClose, bidId }: Props) {
  const navigate = useNavigate();
  const { mutate: deleteBidReport } = useDeleteBidReport({
    onSuccess: async () => {
      await navigate(bidId ? appRoutes.bidDetail(bidId) : appRoutes.home);
    },
  });

  const { logEvent } = useTracking();

  return (
    <Modal
      title="About to delete report"
      open={isOpen}
      centered
      closable={false}
      footer={
        <div className={css.footer}>
          <Button buttonType="secondary" onClick={onClose}>
            Keep report
          </Button>
          <Button
            buttonStyle="destructive"
            type="submit"
            onClick={() => {
              logEvent(EventNames.qualificationReportDeleted, {
                "Action type": "Button clicked",
              });
              deleteBidReport({ id: bidReportId });
            }}
          >
            Delete report
          </Button>
        </div>
      }
      onCancel={onClose}
    >
      Are you sure you want to delete this report? This action cannot be undone.
    </Modal>
  );
}

export function RegenerateAnswersModal({
  isOpen,
  modalText,
  onClose,
  onRegenerate,
}: {
  isOpen: boolean;
  modalText: string;
  onClose: () => void;
  onRegenerate: () => void;
}) {
  return (
    <Modal
      title="Regenerate report"
      open={isOpen}
      centered
      closable={false}
      footer={
        <div className={css.footer}>
          <Button buttonType="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={() => {
              onClose();
              onRegenerate();
            }}
          >
            Regenerate report
          </Button>
        </div>
      }
      onCancel={onClose}
    >
      {modalText}
    </Modal>
  );
}

export function DeleteBidDocumentModal({
  documentId,
  isOpen,
  onClose,
}: {
  documentId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { message } = App.useApp();

  const { logEvent } = useTracking();
  const { mutate: deleteDocuments } = useDeleteCustomerDocuments({
    onSuccess: async (data) => {
      logEvent(EventNames.qualificationReportDeleted, {
        "Action type": "Button clicked",
        "Deleted document name": data.deleteCustomerDocuments[0].fileName,
      });
    },
    onError: (error, vars) => {
      console.error(`Error deleting bid document: ${JSON.stringify(error)}`);
      message.error(`Failed to delete document ${vars.request.ids[0]}`);
    },
  });

  return (
    <Modal
      title="About to delete document"
      open={isOpen}
      closable={false}
      centered
      footer={
        <div className={css.footer}>
          <Button buttonType="secondary" onClick={onClose}>
            Keep document
          </Button>
          <Button
            buttonStyle="destructive"
            type="submit"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              deleteDocuments({
                request: { ids: [documentId], documentType: DeleteDocumentType.BidDocument },
              });
            }}
          >
            Delete document
          </Button>
        </div>
      }
      onCancel={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }}
    >
      Are you sure you want to delete this document? It will remove any source references to it in
      the report.
    </Modal>
  );
}
